import React, { useState, useContext, useEffect, useMemo } from 'react';
import moment from "moment-timezone";
import Swal from 'sweetalert2';

import { createEstructuraOPD, createEstructuraProducto, estructuraImputaciónRRHH, estructuraTarea } from '../../utils/estructuras';
import { PRIORITY, PRIORITYIN } from '../../utils/priority';
import opdContext from '../../context/opdContext/opdContext';
import templateProduccionContext from '../../context/templateProduccionContext/templateProduccionContext';
import activoContext from '../../context/activoContex/activoContex';
import ProductosSubtabla from '../productosSubtabla/ProductosSubtabla';
import ImputacionRRHHSubtabla from '../imputacionRRHHSubtabla/ImputacionRRHHSubtabla';
import TareasSubtabla from '../tareasSubtabla/TareasSubtabla';
import SelectControl from '../SelecControl/SelectControl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackspace, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import BtnArchivos from '../BtnArchivos/BtnArchivos';
import { addFiles, deleteFile } from '../../api/transactionDocuments';
import clienteAxios from '../../config/axios';

import './OPDForm.css';

const OPDForm = ({ opdToModify, cerrar, usuario, files = [] }) => {
    const isEditMode = opdToModify && Object.keys(opdToModify).length > 0;

    const [opd, setOpd] = useState({ ...createEstructuraOPD() });
    const [tablaSeleccionada, setTablaSeleccionada] = useState('templateProduccion');
    const [isSaving, setIsSaving] = useState(false);
    const [mostrarControles, setMostrarControles] = useState(true);
    const [displayOptions, setDisplayOptions] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState(files);

    const { agregarOPD, modificarOPD } = useContext(opdContext);
    const { templatesProduccionList } = useContext(templateProduccionContext);
    const { activoList } = useContext(activoContext);

    const optionsActivosProduccion = useMemo(() => {
        if (!activoList) return [];
        return activoList
            .filter(a => a.habilitadoProduccion === true)
            .map(a => ({
                value: a.name,
                label: `${a.codActivo} - ${a.name}`,
                activoID: a._id,
                activoCod: a.codActivo
            }));
    }, [activoList]);

    const templateVacio = { value: '', label: '', codTemplate: '', name: '', unidades: '', metros: '', kilos: '' };

    const templatesOptions = useMemo(() => {
        if (!templatesProduccionList) return [];
        return templatesProduccionList.map(t => ({
            value: t._id,
            label: `${t.codTemplate} - ${t.name}`,
            codTemplate: t.codTemplate,
            name: t.name,
            tareas: t.tareas || []
        }));
    }, [templatesProduccionList]);

    const getTareasDelTemplate = (templateValue) => {
        if (!templateValue || !templatesProduccionList) return [];
        const template = templatesProduccionList.find(t => t._id === templateValue);
        return template?.tareas || [];
    };

    const calcularCargaTiempo = (unidadMedida, unidades, metros, kilos, horasCarga) => {
        let cantidad = 0;
        if (unidadMedida === 'UNI') cantidad = parseFloat(unidades) || 0;
        else if (unidadMedida === 'MT') cantidad = parseFloat(metros) || 0;
        else if (unidadMedida === 'KG') cantidad = parseFloat(kilos) || 0;
        const horas = parseFloat(horasCarga) || 0;
        return (cantidad * horas).toFixed(4);
    };

    useEffect(() => {
        setSelectedFiles(files);
    }, [files]);

    useEffect(() => {
        if (isEditMode) {
            let opdModelado = { ...opdToModify };
            let productosModelados = (opdToModify.products && opdToModify.products.length > 0)
                ? opdToModify.products.slice()
                : [{ ...createEstructuraProducto() }];
            if (productosModelados.length > 0 && productosModelados[productosModelados.length - 1].artCod !== '') {
                productosModelados.push({ ...createEstructuraProducto() });
            }
            opdModelado.productos = productosModelados;

            let templatesModelados = (opdToModify.templatesProduccion && opdToModify.templatesProduccion.length > 0)
                ? opdToModify.templatesProduccion.map(t => ({ ...t }))
                : [{ ...templateVacio }];
            if (templatesModelados.length > 0 && templatesModelados[templatesModelados.length - 1].value !== '') {
                templatesModelados.push({ ...templateVacio });
            }
            opdModelado.templatesProduccion = templatesModelados;

            // Modelar imputaciones RRHH
            let imputacionesModeladas = (opdToModify.imputaciones && opdToModify.imputaciones.length > 0)
                ? opdToModify.imputaciones.slice()
                : [{ ...estructuraImputaciónRRHH }];
            if (imputacionesModeladas.length > 0 && imputacionesModeladas[imputacionesModeladas.length - 1].legajo?.label !== '') {
                imputacionesModeladas.push({ ...estructuraImputaciónRRHH });
            }
            opdModelado.imputaciones = imputacionesModeladas;

            // Modelar tareas
            let tareasModeladas = (opdToModify.tareas && opdToModify.tareas.length > 0)
                ? opdToModify.tareas.map((t) => ({
                    ...t,
                    label: t.name || t.label,
                    value: t.codTarea || t.value,
                    activo: t.codActivo ? { id: t.activoIDRef || '', value: t.codActivo, label: t.activoNombre || t.codActivo } : t.activo || null,
                    fromTemplate: t.fromTemplate || '',
                    unidadMedida: t.unidadMedida || '',
                    horasCarga: t.horasCarga || ''
                }))
                : [{ ...estructuraTarea }];
            if (tareasModeladas.length > 0 && tareasModeladas[tareasModeladas.length - 1].label !== '') {
                tareasModeladas.push({ ...estructuraTarea });
            }
            opdModelado.tareas = tareasModeladas;

            setOpd(opdModelado);
        }
    }, [opdToModify]);

    const handleChangeTitle = (value) => {
        setOpd({ ...opd, title: value });
    };

    const handleChangeFecMov = (value) => {
        let dataMoment = moment(value).tz("America/Argentina/Buenos_Aires");
        setOpd({ ...opd, fecMov: dataMoment });
    };

    const handleChangeDeliveryDate = (value) => {
        let dataMoment = value ? moment(value).tz("America/Argentina/Buenos_Aires") : null;
        setOpd({ ...opd, deliveryDate: dataMoment });
    };

    const handleChangeNotePubl = (value) => {
        setOpd({ ...opd, notePubl: value });
    };

    const handleChangeState = (value) => {
        setOpd({ ...opd, state: value });
    };

    const handleGeocodificar = async () => {
        try {
            const resp = await clienteAxios.get(`/geocode?address=${encodeURIComponent(opd.direccion)}`);
            const { lat, lng } = resp.data.data;
            setOpd({ ...opd, lat, lng });
        } catch (err) {
            console.log('Error geocodificación:', err.response?.data || err.message);
            const msg = err.response?.data?.message || 'Verifique la dirección ingresada';
            Swal.fire({ icon: 'warning', title: 'No se pudo geocodificar', text: msg });
        }
    };

    const displayOpciones = () => {
        setDisplayOptions(!displayOptions);
    };

    const seleccionarPriority = (priorIndex) => {
        displayOpciones();
        setOpd({ ...opd, priority: priorIndex });
    };

    const handleChangeProductos = (prod = {}, i, property = '', value = '') => {
        setOpd((prevOpd) => {
            const actualList = (prevOpd.productos != undefined) ? prevOpd.productos.slice() : [{ ...createEstructuraProducto() }];
            if (property !== '' && value !== '' && Object.keys(prod).length === 0) {
                actualList[i][property] = value;
            } else if (property !== '') {
                actualList[i][property] = value;
            } else {
                const prodTemp = { ...prod, user_id: (Math.random() * 100).toString() };
                if (prod.tipPro !== '') {
                    if (actualList[i].tipPro !== '') {
                        actualList[i] = prodTemp;
                    } else {
                        const nuevaEstructura = { ...createEstructuraProducto() };
                        actualList[i] = prodTemp;
                        actualList.push(nuevaEstructura);
                    }
                }
            }
            return { ...prevOpd, productos: actualList };
        });
    };

    const handleDeleteProd = (prod, index) => {
        let actualProductos = opd.productos.slice();
        if (prod.tipPro !== '') {
            let productosToUpdate = actualProductos.filter((p, i) => i !== index);
            setOpd({ ...opd, productos: productosToUpdate });
        }
    };

    const handleChangeTemplate = (selectedOption, index) => {
        if (!selectedOption || !selectedOption.value) return;

        const yaExiste = opd.templatesProduccion?.some((t, i) => t.value === selectedOption.value && i !== index);
        if (yaExiste) {
            Swal.fire({
                title: 'Atencion',
                text: 'Este template ya fue agregado',
                icon: 'warning'
            });
            return;
        }

        const nuevosTemplates = [...(opd.templatesProduccion || [])];
        const currentTemplate = nuevosTemplates[index];
        const unidades = currentTemplate.unidades || '';
        const metros = currentTemplate.metros || '';
        const kilos = currentTemplate.kilos || '';

        nuevosTemplates[index] = {
            ...selectedOption,
            unidades,
            metros,
            kilos
        };

        if (index === nuevosTemplates.length - 1) {
            nuevosTemplates.push({ ...templateVacio });
        }

        // Cargar tareas del template en la pestaña de tareas
        const tareasDelMaestro = getTareasDelTemplate(selectedOption.value);
        let tareasActuales = [...(opd.tareas || [])];

        // Remover la última fila vacía si existe
        if (tareasActuales.length > 0 && (!tareasActuales[tareasActuales.length - 1].label || tareasActuales[tareasActuales.length - 1].label === '')) {
            tareasActuales = tareasActuales.slice(0, -1);
        }

        const nuevasTareasTemplate = tareasDelMaestro
            .filter(t => !tareasActuales.some(ta => ta.value === (t.codTarea || t.value) && ta.value !== '' && ta.fromTemplate === selectedOption.value))
            .map(t => ({
                value: t.codTarea || t.value || '',
                label: t.name || t.label || '',
                name: t.name || '',
                id: t.id || '',
                taskDescrp: t.taskDescrp || '',
                activo: t.codActivo ? {
                    id: t.activoIDRef || '',
                    value: t.codActivo,
                    label: t.activoNombre || t.codActivo
                } : null,
                cargaTiempo: calcularCargaTiempo(t.unidadMedida, unidades, metros, kilos, t.horasCarga),
                fromTemplate: selectedOption.value,
                unidadMedida: t.unidadMedida || '',
                horasCarga: t.horasCarga || ''
            }));

        tareasActuales = [...tareasActuales, ...nuevasTareasTemplate, { ...estructuraTarea }];

        setOpd({ ...opd, templatesProduccion: nuevosTemplates, tareas: tareasActuales });
    };

    const handleChangeTemplateCantidades = (index, campo, value) => {
        const nuevosTemplates = [...(opd.templatesProduccion || [])];
        const template = nuevosTemplates[index];

        const unidades = campo === 'unidades' ? value : (template.unidades || '');
        const metros = campo === 'metros' ? value : (template.metros || '');
        const kilos = campo === 'kilos' ? value : (template.kilos || '');

        nuevosTemplates[index] = {
            ...template,
            unidades,
            metros,
            kilos
        };

        // Recalcular cargaTiempo de las tareas que vinieron de este template
        const tareasRecalculadas = (opd.tareas || []).map(t => {
            if (t.fromTemplate === template.value && t.unidadMedida && t.horasCarga) {
                return {
                    ...t,
                    cargaTiempo: calcularCargaTiempo(t.unidadMedida, unidades, metros, kilos, t.horasCarga)
                };
            }
            return t;
        });

        setOpd({ ...opd, templatesProduccion: nuevosTemplates, tareas: tareasRecalculadas });
    };

    const handleRemoveTemplate = (index) => {
        const templateToRemove = opd.templatesProduccion[index];
        const nuevosTemplates = opd.templatesProduccion.filter((_, i) => i !== index);
        if (nuevosTemplates.length === 0 || nuevosTemplates[nuevosTemplates.length - 1].value !== '') {
            nuevosTemplates.push({ ...templateVacio });
        }

        // Eliminar tareas que vinieron de este template
        let tareasActuales = [...(opd.tareas || [])];
        if (templateToRemove && templateToRemove.value) {
            tareasActuales = tareasActuales.filter(t => t.fromTemplate !== templateToRemove.value);
            if (tareasActuales.length === 0 || (tareasActuales[tareasActuales.length - 1].label && tareasActuales[tareasActuales.length - 1].label !== '')) {
                tareasActuales.push({ ...estructuraTarea });
            }
        }

        setOpd({ ...opd, templatesProduccion: nuevosTemplates, tareas: tareasActuales });
    };

    // Handlers para imputaciones RRHH
    const handleChangeImputaciones = (categoria = {}, i, property = '', value = '') => {
        setOpd((prevOpd) => {
            const actualList = prevOpd.imputaciones ? prevOpd.imputaciones.slice() : [{ ...estructuraImputaciónRRHH }];

            // Cuando se selecciona un legajo desde el SelectControl (property = 'legajo', value = selectedOption)
            if (property === 'legajo' && value && value.label) {
                const eraFilaVacia = !actualList[i].legajo?.label || actualList[i].legajo?.label === '';
                actualList[i] = { ...actualList[i], legajo: value };
                // Si era una fila vacía, agregar nueva fila vacía
                if (eraFilaVacia) {
                    actualList.push({ ...estructuraImputaciónRRHH });
                }
            } else if (property !== '' && Object.keys(categoria).length === 0) {
                // Cambio de cantHoras o textAdic
                actualList[i][property] = value;
            } else if (property !== '') {
                actualList[i][property] = value;
            } else {
                // Cuando se selecciona un legajo del select (modo alternativo)
                const imputacionTemp = {
                    ...actualList[i],
                    legajo: categoria
                };
                if (categoria.label !== '') {
                    if (actualList[i].legajo?.label !== '') {
                        actualList[i] = imputacionTemp;
                    } else {
                        actualList[i] = imputacionTemp;
                        actualList.push({ ...estructuraImputaciónRRHH });
                    }
                }
            }

            // Calcular importe total RRHH
            let importeTotalRRHH = 0;
            actualList.forEach((imp) => {
                if (imp.legajo && imp.legajo.basicoHora) {
                    importeTotalRRHH += imp.cantHoras * imp.legajo.basicoHora;
                }
            });

            return { ...prevOpd, imputaciones: actualList, importeTotalRRHH };
        });
    };

    const handleDeleteImputaciones = (imputacion, index) => {
        setOpd((prevOpd) => {
            let imputaciones = prevOpd.imputaciones ? prevOpd.imputaciones.slice() : [];
            if (imputaciones.length > 0 && index > -1 && imputacion.legajo?.label !== '') {
                imputaciones = imputaciones.filter((imp, i) => i !== index);
            }
            return { ...prevOpd, imputaciones };
        });
    };

    const completedTasks = opd.tareas?.filter(t => t.done === true).length;

    const handleChangeCheckTask = (e, i) => {
        let tareas = opd.tareas.slice();
        tareas[i].done = e.target.checked;
        tareas[i].porcentajeCumplimiento = e.target.checked ? '100' : '0';
        setOpd({ ...opd, tareas });
    };

    const handleSave = async () => {
        if (!opd.title || opd.title.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Titulo requerido',
                text: 'Por favor ingrese un titulo para la OPD'
            });
            return;
        }

        if (!opd.deliveryDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Fecha de entrega requerida',
                text: 'Por favor ingrese una fecha de entrega para la OPD'
            });
            return;
        }

        setIsSaving(true);

        try {
            const productosToSave = opd.productos.filter(p => p.artCod && p.artCod !== '');
            const templatesToSave = (opd.templatesProduccion || []).filter(t => t.value && t.value !== '');
            const imputacionesToSave = (opd.imputaciones || []).filter(i => i.legajo && i.legajo.label !== '');
            const tareasToSave = (opd.tareas || []).filter(t => t.label && t.label !== '');

            const filesToUpload = selectedFiles.filter(f => f.accion === 'ADD');
            const filesToDelete = selectedFiles.filter(f => f.accion === 'DELETE');

            if (isEditMode) {
                const updates = {
                    title: opd.title,
                    fecMov: opd.fecMov,
                    deliveryDate: opd.deliveryDate,
                    notePubl: opd.notePubl,
                    state: opd.state,
                    priority: opd.priority ?? 0,
                    revisadaProduccion: opd.revisadaProduccion ?? false,
                    templatesProduccion: templatesToSave,
                    importeTotalRRHH: opd.importeTotalRRHH ?? 0,
                    direccion: opd.direccion || '',
                    lat: opd.lat || null,
                    lng: opd.lng || null
                };
                await modificarOPD(opd._id, updates, productosToSave, imputacionesToSave, tareasToSave);

                if (filesToUpload.length > 0) {
                    const formData = new FormData();
                    filesToUpload.forEach(f => formData.append('files', f.dato));
                    formData.append('prefijo', `OPD-${opd.tDocNroFor}`);
                    await addFiles(formData);
                }
                if (filesToDelete.length > 0) {
                    for (const file of filesToDelete) await deleteFile(file.name);
                }

                Swal.fire({
                    icon: 'success',
                    title: 'OPD actualizada',
                    text: `La OPD ${opd.tDocNroFor} fue actualizada correctamente.`
                });
            } else {
                const opdToCreate = {
                    title: opd.title,
                    fecMov: opd.fecMov,
                    deliveryDate: opd.deliveryDate,
                    notePubl: opd.notePubl,
                    state: opd.state || 'PENDIENTE',
                    priority: opd.priority ?? 0,
                    revisadaProduccion: opd.revisadaProduccion ?? false,
                    quantity: opd.quantity || 1,
                    tDocUserID: usuario._id,
                    userName: usuario.userName,
                    templatesProduccion: templatesToSave,
                    importeTotalRRHH: opd.importeTotalRRHH ?? 0,
                    direccion: opd.direccion || '',
                    lat: opd.lat || null,
                    lng: opd.lng || null
                };
                const opdCreated = await agregarOPD(opdToCreate, productosToSave, imputacionesToSave, tareasToSave);

                if (filesToUpload.length > 0) {
                    const formData = new FormData();
                    filesToUpload.forEach(f => formData.append('files', f.dato));
                    formData.append('prefijo', `OPD-${opdCreated.tDocNroFor}`);
                    await addFiles(formData);
                }

                Swal.fire({
                    icon: 'success',
                    title: 'OPD creada',
                    text: 'La Orden de Produccion fue creada correctamente.'
                });
            }
            cerrar();
        } catch (error) {
            console.log(error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Ocurrio un error al guardar la OPD'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="opdFormContainer">
            <div className='container-r-list-item-header'>
                <input
                    placeholder='Ingrese un titulo...'
                    className='list-item-header-title-addReq'
                    value={opd.title}
                    onChange={(e) => handleChangeTitle(e.target.value)}
                    autoFocus={true}
                    style={{ fontWeight: "bold" }}
                />
                <div className='container-prioritys-gral'>
                    <div className={opd.priority === 2 ? 'r-list-item-header-priority-urgente margin-right-r' : opd.priority === 1 ? 'r-list-item-header-priority-alta margin-right-r' : 'r-list-item-header-priority-media margin-right-r'} onClick={displayOpciones}>
                        {PRIORITY[opd.priority ?? 0]}
                    </div>
                    {displayOptions &&
                        <div className='options-container-fr margin-right-r'>
                            <div className='options-container-fr-back'>
                                {PRIORITY.map((prior) => (
                                    <div key={prior} className={PRIORITYIN[prior] === 2 ? 'r-list-item-header-priority-urgente' : PRIORITYIN[prior] === 1 ? 'r-list-item-header-priority-alta' : 'r-list-item-header-priority-media'} onClick={() => seleccionarPriority(PRIORITYIN[prior])}>{prior}</div>
                                ))}
                            </div>
                        </div>
                    }
                </div>
                <button
                    className='ocultarControlesBtn'
                    onClick={() => setMostrarControles(!mostrarControles)}
                >
                    <FontAwesomeIcon icon={mostrarControles ? faEyeSlash : faEye} />
                    <span>{mostrarControles ? 'Ocultar controles' : 'Mostrar controles'}</span>
                </button>
                <button className='minimizarBtn' onClick={() => cerrar()}>X</button>
            </div>

            {mostrarControles && <>
                <div className="container-AddReq-item-activoAndDeliveryDate">
                    {isEditMode && (
                        <div className='comprobanteDiv'>
                            <span>Comprobante: <label>{`OPD - ${opd.tDocNroFor}`}</label></span>
                        </div>
                    )}

                    <div className="container-r-item-title-details">
                        <label>Fecha de Movimiento</label>
                        <input
                            type="date"
                            className="label-AddReq-fecha"
                            value={moment(opd.fecMov).format("YYYY-MM-DD")}
                            onChange={(e) => handleChangeFecMov(e.target.value)}
                        />
                    </div>

                    <div className="container-r-item-title-details">
                        <label>Fecha de Entrega</label>
                        <input
                            type="date"
                            className="label-AddReq-fecha"
                            value={opd.deliveryDate ? moment(opd.deliveryDate).format("YYYY-MM-DD") : ''}
                            onChange={(e) => handleChangeDeliveryDate(e.target.value)}
                        />
                    </div>

                    <div className="container-r-item-title-details checkboxRevisada">
                        <label>
                            <input
                                type="checkbox"
                                checked={opd.revisadaProduccion || false}
                                onChange={(e) => setOpd({ ...opd, revisadaProduccion: e.target.checked })}
                            />
                            ya REVISADA para Producción
                        </label>
                    </div>

                </div>

                <div className='container-AddReq-item-gral'>
                    <div className='container-AddReq-item-details-text'>
                        <div className='container-r-item-title-details'>Texto Adicional</div>
                        <div className='container-input-texto'>
                            <div className='r-list-item-details-data-v'>
                                <textarea
                                    placeholder='Ingrese texto adicional'
                                    className='r-item-text-input'
                                    style={{ fontWeight: 600 }}
                                    value={opd.notePubl || ''}
                                    onChange={(e) => handleChangeNotePubl(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className='container-AddReq-item-gral'>
                    <div className='container-AddReq-item-details-text'>
                        <div className='container-r-item-title-details'>Dirección</div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                                placeholder='Ej: Av. Corrientes 1234, CABA'
                                className='r-item-text-input'
                                style={{ fontWeight: 600, flex: 1 }}
                                value={opd.direccion || ''}
                                onChange={(e) => setOpd({ ...opd, direccion: e.target.value, lat: null, lng: null })}
                            />
                            {opd.direccion && !opd.lat && (
                                <button type="button" className='geocodeBtnOPD' onClick={handleGeocodificar}>
                                    Validar
                                </button>
                            )}
                            {opd.lat && <span style={{ color: 'green', fontSize: '0.8rem' }}>&#10003; Ubicación validada</span>}
                        </div>
                    </div>
                </div>
            </>}

            <div className="tablasHijasDiv">
                <div className="opcionesHijasDiv">
                    <button
                        className={(tablaSeleccionada === 'templateProduccion') ? 'opcionHijaBtn selected' : 'opcionHijaBtn'}
                        onClick={() => setTablaSeleccionada('templateProduccion')}
                    >
                        Templates de Produccion
                    </button>
                    <button
                        className={(tablaSeleccionada === 'tareas') ? 'opcionHijaBtn selected' : 'opcionHijaBtn'}
                        onClick={() => setTablaSeleccionada('tareas')}
                    >
                        Tareas
                    </button>
                    <button
                        className={(tablaSeleccionada === 'componentes') ? 'opcionHijaBtn selected' : 'opcionHijaBtn'}
                        onClick={() => setTablaSeleccionada('componentes')}
                    >
                        Materiales
                    </button>
                    <button
                        className={(tablaSeleccionada === 'imputaciones') ? 'opcionHijaBtn selected' : 'opcionHijaBtn'}
                        onClick={() => setTablaSeleccionada('imputaciones')}
                    >
                        Imputaciones RRHH
                    </button>
                </div>
                <ProductosSubtabla
                    productos={opd.productos ?? []}
                    handleChangeProductos={handleChangeProductos}
                    componentePadre='FormOPD'
                    handleDeleteProd={handleDeleteProd}
                    tablaSeleccionada={tablaSeleccionada}
                />
                <ImputacionRRHHSubtabla
                    imputaciones={opd.imputaciones ?? []}
                    tablaSeleccionada={tablaSeleccionada}
                    handleChangeImputaciones={handleChangeImputaciones}
                    handleDeleteImputaciones={handleDeleteImputaciones}
                    componentePadre='FormOPD'
                />
                <TareasSubtabla
                    tareas={opd.tareas ?? []}
                    setState={setOpd}
                    tablaSeleccionada={tablaSeleccionada}
                    componentePadre='FormOPD'
                    tipo='tareaOT'
                    completedTasks={completedTasks}
                    handleChangeCheckTask={handleChangeCheckTask}
                    tDocID={opd._id || ''}
                    optionsActivosProduccion={optionsActivosProduccion}
                />
                {tablaSeleccionada === 'templateProduccion' && (
                    <div className="templatesProduccionSubtabla">
                        <div className="headerSubtablaTemplates">
                            <label></label>
                            <label>Template de Produccion</label>
                            <label>Unidades</label>
                            <label>Metros</label>
                            <label>Kilos</label>
                        </div>
                        <div className="bodyTableTemplates">
                            {(opd.templatesProduccion || []).map((template, index) => (
                                <div key={index} className="templateRowWrapper">
                                    <div className="templateRowSubtabla">
                                        <FontAwesomeIcon
                                            icon={faBackspace}
                                            color="red"
                                            className="iconDeleteTemplate"
                                            onClick={() => handleRemoveTemplate(index)}
                                        />
                                        <SelectControl
                                            placeholder="Seleccionar template..."
                                            modo="Nuevo"
                                            handleChengeSelect={handleChangeTemplate}
                                            optionsSelect={templatesOptions}
                                            valueSeleccionada={template}
                                            tipo="odtToAssociate"
                                            index={index}
                                        />
                                        <input
                                            type="number"
                                            value={template.unidades || ''}
                                            onChange={(e) => handleChangeTemplateCantidades(index, 'unidades', e.target.value)}
                                            placeholder="0"
                                            min="0"
                                        />
                                        <input
                                            type="number"
                                            value={template.metros || ''}
                                            onChange={(e) => handleChangeTemplateCantidades(index, 'metros', e.target.value)}
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                        />
                                        <input
                                            type="number"
                                            value={template.kilos || ''}
                                            onChange={(e) => handleChangeTemplateCantidades(index, 'kilos', e.target.value)}
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <BtnArchivos files={files} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles}/>

            <div className='container-button-rq-save-new'>
                <button
                    className='button-rq-save-new'
                    style={{ backgroundColor: '#3D4D87' }}
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </div>
    );
};

export default OPDForm;
